import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { PopupModule } from '@progress/kendo-angular-popup';
import { SVGIcon, plusIcon, xIcon } from '@progress/kendo-svg-icons';
import { UserComponent } from './component/user/user.component';
import { AdminAppbarComponent } from '../layout/components/admin-appbar/admin-appbar.component';
import { PopupHostComponent } from '../../../core/components/popup-host.component';
import { DatatableComponent } from '../../../core/components/datatable/datatable.component';
import { IUser } from '../../../core/models/entities';
import { User } from '../../../core/models/user';
import { environment } from '../../../../environments/environment';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    DropDownListModule,
    ButtonsModule,
    PopupModule,
    AdminAppbarComponent,
    UserComponent,
    DatatableComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './users.page.html',
  styleUrls: ['./users.page.scss'],
})
export class UsersPage extends PopupHostComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public xIcon: SVGIcon = xIcon;
  public plusIcon: SVGIcon = plusIcon;

  public statuses = [
    { text: 'Active', value: 1 },
    { text: 'Disabled', value: 0 },
  ];
  show: boolean | undefined;

  public datatableUrl = `${environment.apiUrl}/v1/user/administrative/user/`;

  private statusFilter: number | null = null;

  public initialFilters = this.buildFilters();

  public datatableColumns = [
    { data: 'id', name: 'id', title: 'Id', type: 'number' },
    { data: 'first_name', name: 'first_name', title: 'First', type: 'string', render: DataTable.render.text() },
    { data: 'last_name', name: 'last_name', title: 'Last', type: 'string', render: DataTable.render.text() },
    {
      data: 'is_active',
      name: 'is_active',
      title: 'Status',
      type: 'string',
      render: (data: boolean) =>
        data ? '<span class="badge bg-success">Active</span>' : '<span class="badge bg-danger">Inactive</span>',
    },
    {
      data: 'email',
      name: 'email',
      title: 'Email',
      type: 'string',
      render: (data: string) => {
        const email = DataTable.util.escapeHtml(data ?? '');
        return email ? `<a class="text-decoration-underline" href="mailto:${email}">${email}</a>` : '';
      },
    },
    {
      data: null,
      name: 'userprofile__primary_organization__name',
      title: 'Organization',
      type: 'string',
      render: (_data: unknown, _type: unknown, row: IUser) =>
        DataTable.util.escapeHtml((row as any).primary_organization?.name ?? 'Default Organization Name'),
    },
    {
      data: 'last_login',
      name: 'last_login',
      title: 'Last Login',
      type: 'string',
      render: (data: string) => (data ? new Date(data).toLocaleString() : ''),
    },
  ];

  private buildFilters(): Record<string, string> {
    const filters: Record<string, string> = { $orderby: 'first_name' };

    if (this.statusFilter != null) {
      filters['$filter'] = `is_active eq ${this.statusFilter}`;
    }
    return filters;
  }

  public statusChange($event: { text: string; value: number | null }): void {
    this.statusFilter = $event?.value ?? null;
    this.table?.setFilters(this.buildFilters());
  }

  public addUser(): void {
    this.togglePopup();
  }

  public editUser(row: unknown): void {
    this.togglePopup(row as IUser);
  }

  public togglePopup(user: IUser = User.createUser()): void {
    const options = {
      animate: { type: 'slide', direction: 'left', duration: 200 },
      anchorAlign: { horizontal: 'right', vertical: 'top' },
      popupAlign: { horizontal: 'right', vertical: 'top' },
    };

    this.openPopup(this.bodyElement, UserComponent, options);

    const userComponent = this.popupRef?.content.instance as UserComponent;

    if (user) userComponent.user.set(user);

    userComponent.close.subscribe(() => {
      this.table?.reload();
      this.closePopup();
    });
  }
}
